import classNames from './Waveform.module.scss';
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import SVG from 'react-inlinesvg';
import soundPlay from '@/assets/svg/white_speaker.svg';
import speakerIcon from '@/assets/svg/speaker.svg';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { gameActions } from '@/store/reducers/game';
import UseUnitsAssets from '@/hooks/UseUnitsAssets';
import { unitSelector } from '@/store/reducers/unit';
import { useAppSelector } from '@/store';
import commonUtils from '@/utils/common';

type WaveFormProps = {
  audio: string;
};

const WaveForm = ({ audio }: WaveFormProps) => {
  const { unitsAssets } = UseUnitsAssets();
  const { selectedUnit } = useAppSelector(unitSelector);
  const containerRef = useRef<any>('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const waveSurferRef = useRef<WaveSurfer | null>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const color = unitsAssets[selectedUnit?.order || 1].modalBorderColor;
  const { isSafari, isIOS } = commonUtils.getBrowserData();

  useEffect(() => {
    dispatch(gameActions.toggleSoundWaveStatus(false));
    setIsReady(false);
    setIsPlaying(false);
    waveSurferRef.current?.destroy();
    (containerRef.current as HTMLElement).innerHTML = '';

    if (!containerRef.current.innerHTML) {
      const waveSurfer = WaveSurfer.create({
        container: containerRef.current,
        cursorWidth: 0,
        barWidth: 2,
        progressColor: color,
        url: audio,
        height: 70,
        ...(isSafari || isIOS
          ? {
              backend: 'MediaElement',
              mediaControls: false,
              autoplay: false,
            }
          : {}),
      });

      waveSurfer.on('ready', () => {
        dispatch(gameActions.toggleSoundWaveStatus(true));
        setIsReady(true);
        waveSurferRef.current = waveSurfer;
      });

      waveSurfer.on('play', () => {
        setIsPlaying(true);
      });

      waveSurfer.on('pause', () => {
        setIsPlaying(false);
      });

      waveSurfer.on('finish', () => {
        setIsPlaying(false);
        // Reset to beginning for Safari compatibility
        if (isSafari || isIOS) {
          setTimeout(() => {
            waveSurfer.seekTo(0);
          }, 100);
        }
      });
    }
  }, [audio, color, isSafari, isIOS]);

  const handlePlayPause = async () => {
    if (!waveSurferRef.current || !isReady) {
      return;
    }

    try {
      // For Safari/iOS, ensure we have user interaction and handle audio context
      if (isSafari || isIOS) {
        if (
          !isPlaying &&
          waveSurferRef.current.getCurrentTime() >= waveSurferRef.current.getDuration() - 0.1
        ) {
          waveSurferRef.current.seekTo(0);
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // For Safari, try to resume any suspended audio context
        try {
          const audioElement = waveSurferRef.current.getMediaElement();
          if (audioElement && audioElement.readyState >= 2) {
            await audioElement.play();
            return;
          }
        } catch (audioError) {
          console.log('Audio element play failed, trying WaveSurfer playPause');
        }
      }

      await waveSurferRef.current.playPause();
    } catch (error) {
      console.error('Play/pause error:', error);
      try {
        if (!isPlaying) {
          await waveSurferRef.current.play();
        } else {
          waveSurferRef.current.pause();
        }
      } catch (fallbackError) {
        console.error('Fallback play/pause error:', fallbackError);
      }
    }
  };

  return (
    <div className={classes(classNames.waveform)}>
      <button
        className={classes(classNames.playBtn)}
        style={{ backgroundColor: isPlaying ? color : 'white' }}
        aria-label={t('play_sound').toString()}
        onClick={handlePlayPause}
        type='button'
        disabled={!isReady}
      >
        <SVG src={isPlaying ? soundPlay : speakerIcon} color={isPlaying ? 'white' : undefined} />
      </button>

      <div
        ref={containerRef}
        className='ignore_zoom'
        style={{
          color: color,
          flex: 1,
        }}
      />
    </div>
  );
};

export default WaveForm;
